import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { FileDown, Settings2, TrendingUp, TrendingDown, Fuel, Users, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function currentPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function fmtR(n) {
  const v = Number(n) || 0;
  return `R ${v.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function monthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    opts.push({ value: val, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` });
  }
  return opts;
}

function yearOptions() {
  const now = new Date().getFullYear();
  return [now, now - 1, now - 2].map((y) => ({ value: String(y), label: `Year ${y}` }));
}

export default function SparesPnLPanel() {
  const [mode, setMode] = useState('month'); // 'month' | 'year'
  const [period, setPeriod] = useState(currentPeriod());
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({ spares_delivery_vehicles: [], available_registrations: [] });
  const [newReg, setNewReg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = mode === 'year' ? { year } : { period };
      const res = await axios.get(`${API_URL}/api/auto-spares/pnl`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load P&L');
    } finally {
      setLoading(false);
    }
  }, [mode, period, year]);

  const loadSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/auto-spares/pnl/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(res.data);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleDownloadPdf = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = mode === 'year' ? { year } : { period };
      const res = await axios.get(`${API_URL}/api/auto-spares/pnl/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = mode === 'year' ? `jsm-spares-pnl-${year}.pdf` : `jsm-spares-pnl-${period}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('P&L PDF downloaded');
    } catch (e) {
      toast.error('Failed to download PDF');
    }
  };

  const toggleReg = (reg) => {
    const cur = new Set(settings.spares_delivery_vehicles);
    if (cur.has(reg)) cur.delete(reg); else cur.add(reg);
    setSettings({ ...settings, spares_delivery_vehicles: Array.from(cur) });
  };

  const addManualReg = () => {
    const r = newReg.trim().toUpperCase();
    if (!r) return;
    if (settings.spares_delivery_vehicles.includes(r)) { toast.info('Already added'); return; }
    setSettings({ ...settings, spares_delivery_vehicles: [...settings.spares_delivery_vehicles, r] });
    setNewReg('');
  };

  const saveSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API_URL}/api/auto-spares/pnl/settings`,
        { spares_delivery_vehicles: settings.spares_delivery_vehicles },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSettings({ ...settings, spares_delivery_vehicles: res.data.spares_delivery_vehicles });
      toast.success('Settings saved');
      setShowSettings(false);
      load();
    } catch (e) {
      toast.error('Save failed');
    }
  };

  if (loading && !data) {
    return <div className="text-slate-300 py-8 text-center">Loading P&L…</div>;
  }
  if (!data) return null;

  const profitColour = data.operating_profit >= 0 ? 'text-emerald-400' : 'text-rose-400';

  return (
    <div className="space-y-4" data-testid="spares-pnl-panel">
      {/* Header controls */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-slate-300 text-xs">View</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="bg-slate-700 border-slate-600 text-white w-32" data-testid="pnl-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700 text-white">
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="year">Year (YTD)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {mode === 'month' ? (
          <div>
            <Label className="text-slate-300 text-xs">Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white w-48" data-testid="pnl-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-white max-h-80 overflow-auto">
                {monthOptions().map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div>
            <Label className="text-slate-300 text-xs">Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white w-40" data-testid="pnl-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-white">
                {yearOptions().map((y) => (
                  <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          onClick={handleDownloadPdf}
          data-testid="download-pnl-pdf-btn"
        >
          <FileDown className="h-4 w-4 mr-2" /> Download PDF
        </Button>
        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogTrigger asChild>
            <Button variant="outline" className="border-slate-500 text-slate-200 hover:bg-slate-700" data-testid="pnl-settings-btn">
              <Settings2 className="h-4 w-4 mr-2" /> Fuel Vehicles
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle>Auto Spares Delivery Vehicles</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-slate-400 mb-2">
              Pick registrations whose Fuel Tracker entries should count as Spares fuel expense.
            </p>
            <div className="max-h-72 overflow-auto border border-slate-700 rounded p-2 space-y-1" data-testid="pnl-settings-list">
              {settings.available_registrations.length === 0 && (
                <div className="text-slate-500 text-sm italic">No fuel entries yet — add a registration manually below.</div>
              )}
              {settings.available_registrations.map((r) => (
                <label key={r} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-700 px-2 py-1 rounded">
                  <input
                    type="checkbox"
                    checked={settings.spares_delivery_vehicles.includes(r)}
                    onChange={() => toggleReg(r)}
                    data-testid={`reg-toggle-${r}`}
                  />
                  <span>{r}</span>
                </label>
              ))}
              {settings.spares_delivery_vehicles
                .filter((r) => !settings.available_registrations.includes(r))
                .map((r) => (
                  <label key={r} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-700 px-2 py-1 rounded">
                    <input type="checkbox" checked onChange={() => toggleReg(r)} />
                    <span>{r} <span className="text-slate-500 text-xs">(manual)</span></span>
                  </label>
                ))}
            </div>
            <div className="flex gap-2 items-end pt-2">
              <div className="flex-1">
                <Label className="text-xs text-slate-300">Add registration manually</Label>
                <Input
                  className="bg-slate-700 border-slate-600 text-white"
                  value={newReg}
                  onChange={(e) => setNewReg(e.target.value)}
                  placeholder="e.g. CA123456"
                  data-testid="manual-reg-input"
                />
              </div>
              <Button variant="outline" onClick={addManualReg} data-testid="manual-reg-add-btn">Add</Button>
            </div>
            <div className="flex justify-end gap-2 pt-3">
              <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={saveSettings} data-testid="save-pnl-settings-btn">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
        <div className="ml-auto text-right">
          <div className="text-xs text-slate-400">{data.period_label}</div>
          <div className="text-[10px] text-slate-500">{data.date_from} → {data.date_to}</div>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-emerald-900/40 border-emerald-700">
          <CardContent className="p-4">
            <div className="text-emerald-300 text-xs uppercase flex items-center gap-1"><DollarSign className="h-3 w-3" /> Revenue</div>
            <div className="text-2xl font-bold text-white mt-1" data-testid="kpi-revenue">{fmtR(data.revenue.total_sales)}</div>
            <div className="text-[10px] text-emerald-200 mt-1">{data.revenue.sales_count} txn · Cash {fmtR(data.revenue.cash_sales)} · Credit {fmtR(data.revenue.credit_sales)}</div>
          </CardContent>
        </Card>
        <Card className="bg-cyan-900/40 border-cyan-700">
          <CardContent className="p-4">
            <div className="text-cyan-300 text-xs uppercase flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Gross Profit</div>
            <div className="text-2xl font-bold text-white mt-1" data-testid="kpi-gross-profit">{fmtR(data.gross_profit)}</div>
            <div className="text-[10px] text-cyan-200 mt-1">Margin {data.gross_margin_pct}% · COGS {fmtR(data.cogs)}</div>
          </CardContent>
        </Card>
        <Card className="bg-orange-900/40 border-orange-700">
          <CardContent className="p-4">
            <div className="text-orange-300 text-xs uppercase flex items-center gap-1"><TrendingDown className="h-3 w-3" /> Total OPEX</div>
            <div className="text-2xl font-bold text-white mt-1" data-testid="kpi-opex">{fmtR(data.total_operating_expenses)}</div>
            <div className="text-[10px] text-orange-200 mt-1">
              Exp {fmtR(data.operating_expenses.total)} · Pay {fmtR(data.payroll.totals.gross)} · Fuel {fmtR(data.fuel_expense.total)}
            </div>
          </CardContent>
        </Card>
        <Card className={data.operating_profit >= 0 ? 'bg-emerald-950 border-emerald-600' : 'bg-rose-950 border-rose-600'}>
          <CardContent className="p-4">
            <div className={`text-xs uppercase flex items-center gap-1 ${data.operating_profit >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
              {data.operating_profit >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              Operating {data.operating_profit >= 0 ? 'Profit' : 'Loss'}
            </div>
            <div className={`text-2xl font-bold mt-1 ${profitColour}`} data-testid="kpi-operating-profit">{fmtR(data.operating_profit)}</div>
            <div className="text-[10px] text-slate-300 mt-1">Margin {data.operating_margin_pct}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Statement body */}
      <Card className="bg-slate-800/60 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg">Profit & Loss Statement</CardTitle>
        </CardHeader>
        <CardContent className="text-white space-y-6">
          {/* Revenue */}
          <section>
            <h3 className="text-emerald-400 font-semibold text-sm uppercase tracking-wide border-b border-emerald-800/50 pb-1 mb-2">Revenue</h3>
            <RowLine label="Cash Sales" amount={data.revenue.cash_sales} indent />
            <RowLine label="Credit Sales (invoiced)" amount={data.revenue.credit_sales} indent />
            <RowLine label={`Total Sales (${data.revenue.sales_count} transactions)`} amount={data.revenue.total_sales} bold />
            <RowLine label="Cost of Goods Sold" amount={-data.cogs} indent negative />
            {data.cogs_detail && (
              <div className="pl-8 text-[11px] text-slate-400 space-y-0.5" data-testid="pnl-cogs-detail">
                <div>Automatic COGS (new sales): {fmtR(data.cogs_detail.automatic)} · Recorded cost (older sales): {fmtR(data.cogs_detail.legacy_recorded)}</div>
                {data.cogs_detail.legacy_sales_missing_cost > 0 && (
                  <div className="text-amber-400" data-testid="pnl-legacy-missing-cost">{data.cogs_detail.legacy_sales_missing_cost} older sales ({fmtR(data.cogs_detail.legacy_sales_missing_cost_value)}) have no cost recorded — gross profit is overstated until reconciled.</div>
                )}
                {data.cogs_detail.sales_flagged_for_cost_review > 0 && (
                  <div className="text-rose-400">{data.cogs_detail.sales_flagged_for_cost_review} new sales are waiting in COGS Review.</div>
                )}
              </div>
            )}
            <RowLine label="GROSS PROFIT" amount={data.gross_profit} bold highlight={data.gross_profit >= 0 ? 'emerald' : 'rose'} />
          </section>

          {/* Operating Expenses */}
          <section>
            <h3 className="text-orange-400 font-semibold text-sm uppercase tracking-wide border-b border-orange-800/50 pb-1 mb-2">Operating Expenses</h3>
            {data.operating_expenses.by_category.length === 0 ? (
              <div className="text-slate-500 italic text-sm">No expenses recorded for this period.</div>
            ) : (
              data.operating_expenses.by_category.map((r) => (
                <RowLine key={r.category} label={r.category} amount={r.amount} indent />
              ))
            )}
            <RowLine label="Sub-total Operating Expenses" amount={data.operating_expenses.total} bold />
            {data.stock_purchases_excluded?.total > 0 && (
              <div className="mt-2 rounded border border-slate-700 bg-slate-900/60 px-3 py-2" data-testid="pnl-stock-excluded">
                <RowLine label="Stock purchases (held as stock, not an expense)" amount={data.stock_purchases_excluded.total} muted />
                <p className="text-[11px] text-slate-500 italic">{data.stock_purchases_excluded.note}</p>
              </div>
            )}
          </section>

          {/* Payroll */}
          <section>
            <h3 className="text-purple-400 font-semibold text-sm uppercase tracking-wide border-b border-purple-800/50 pb-1 mb-2 flex items-center gap-2">
              <Users className="h-4 w-4" /> Payroll (Auto Spares Staff)
            </h3>
            {data.payroll.rows.length === 0 ? (
              <div className="text-slate-500 italic text-sm">No Auto Spares staff configured.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-300">Staff</TableHead>
                      <TableHead className="text-slate-300 text-right">Basic</TableHead>
                      <TableHead className="text-slate-300 text-right">Comm.</TableHead>
                      <TableHead className="text-slate-300 text-right">Allow.</TableHead>
                      <TableHead className="text-slate-300 text-right">OT</TableHead>
                      <TableHead className="text-slate-300 text-right">Incent.</TableHead>
                      <TableHead className="text-slate-300 text-right">Bonus</TableHead>
                      <TableHead className="text-slate-300 text-right">Fuel Allow.</TableHead>
                      <TableHead className="text-slate-300 text-right">Loan</TableHead>
                      <TableHead className="text-slate-300 text-right">Gross</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.payroll.rows.map((r, i) => (
                      <TableRow key={i} className="border-slate-700" data-testid={`payroll-row-${i}`}>
                        <TableCell className="text-white">{r.name}</TableCell>
                        <TableCell className="text-right text-slate-200">{fmtR(r.basic)}</TableCell>
                        <TableCell className="text-right text-slate-200">{fmtR(r.commission)}</TableCell>
                        <TableCell className="text-right text-slate-200">{fmtR(r.allowance)}</TableCell>
                        <TableCell className="text-right text-slate-200">{fmtR(r.overtime)}</TableCell>
                        <TableCell className="text-right text-slate-200">{fmtR(r.incentive)}</TableCell>
                        <TableCell className="text-right text-slate-200">{fmtR(r.target_bonus)}</TableCell>
                        <TableCell className="text-right text-slate-200">{fmtR(r.fuel_allowance)}</TableCell>
                        <TableCell className="text-right text-rose-300">{r.loan_deduction ? `-${fmtR(r.loan_deduction)}` : '—'}</TableCell>
                        <TableCell className="text-right font-semibold text-white">{fmtR(r.gross)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <RowLine label="Sub-total Payroll (Gross)" amount={data.payroll.totals.gross} bold />
          </section>

          {/* Fuel */}
          <section>
            <h3 className="text-blue-400 font-semibold text-sm uppercase tracking-wide border-b border-blue-800/50 pb-1 mb-2 flex items-center gap-2">
              <Fuel className="h-4 w-4" /> Fuel Expenses (Spares delivery vehicles)
            </h3>
            {data.fuel_expense.spares_delivery_vehicles.length === 0 ? (
              <div className="text-slate-500 italic text-sm">
                No delivery vehicles configured for Auto Spares.{' '}
                <button className="text-blue-400 underline" onClick={() => setShowSettings(true)}>Configure now</button>.
              </div>
            ) : data.fuel_expense.by_vehicle.length === 0 ? (
              <div className="text-slate-500 italic text-sm">
                No fuel entries logged for the configured vehicles ({data.fuel_expense.spares_delivery_vehicles.join(', ')}) in this period.
              </div>
            ) : (
              data.fuel_expense.by_vehicle.map((r) => (
                <RowLine key={r.registration} label={r.registration} amount={r.amount} indent />
              ))
            )}
            <RowLine label="Sub-total Fuel Expenses" amount={data.fuel_expense.total} bold />
          </section>

          {/* Total OPEX + Operating Profit */}
          <section className="border-t border-slate-600 pt-3">
            <RowLine label="TOTAL OPERATING EXPENSES" amount={data.total_operating_expenses} bold negative />
            <div className={`mt-2 flex items-center justify-between px-3 py-3 rounded ${data.operating_profit >= 0 ? 'bg-emerald-950 border border-emerald-600' : 'bg-rose-950 border border-rose-600'}`}>
              <span className="text-white font-bold text-lg">OPERATING {data.operating_profit >= 0 ? 'PROFIT' : 'LOSS'}</span>
              <span className={`font-bold text-2xl ${profitColour}`} data-testid="pnl-final-operating-profit">{fmtR(data.operating_profit)}</span>
            </div>
            <div className="text-xs text-slate-400 italic mt-1 text-right">Operating Margin: {data.operating_margin_pct}%</div>
            {data.profit_before_tax !== undefined && <>
              <RowLine label="Finance costs (interest)" amount={-(data.finance_costs || 0)} indent negative />
              <RowLine label="PROFIT BEFORE TAX" amount={data.profit_before_tax} bold highlight={data.profit_before_tax >= 0 ? 'emerald' : 'rose'} />
            </>}
          </section>

          {/* Balance sheet notes */}
          <section className="border-t border-slate-700 pt-3">
            <h3 className="text-slate-400 font-semibold text-sm uppercase tracking-wide mb-2">Balance-Sheet Notes (not part of P&L)</h3>
            <RowLine label="Credit customer payments received (cash-in from debtors)" amount={data.balance_sheet_notes.credit_payments_received} indent muted />
            <RowLine label="Debt repayments paid out (2K2Day / other)" amount={data.balance_sheet_notes.debt_repayments_paid} indent muted />
            <RowLine label="Capital injections received" amount={data.balance_sheet_notes.capital_injections} indent muted />
            <p className="text-[11px] text-slate-500 italic mt-2">{data.balance_sheet_notes.note}</p>
          </section>
        </CardContent>
      </Card>
    </div>
  );
}

function RowLine({ label, amount, bold, indent, negative, highlight, muted }) {
  const cls = [
    'flex justify-between items-center py-1',
    bold ? 'border-t border-slate-700 mt-1 pt-2 font-semibold' : '',
    muted ? 'text-slate-400' : 'text-slate-200',
  ].join(' ');
  const amtColour = highlight === 'emerald'
    ? 'text-emerald-400'
    : highlight === 'rose'
      ? 'text-rose-400'
      : negative
        ? 'text-rose-300'
        : muted ? 'text-slate-400' : 'text-white';
  return (
    <div className={cls} style={{ paddingLeft: indent ? 16 : 0 }}>
      <span>{label}</span>
      <span className={`${amtColour} ${bold ? 'font-bold' : ''} font-mono`}>{fmtR(amount)}</span>
    </div>
  );
}
